import React, { FC, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Course } from '../../lib/interfaces/Course';
import CoursesListItem from './CoursesListItem';
import Loader from '../Simple/Loader/Loader';

import {
	COURSE_FILTERS,
	COURSE_STATUSES,
} from '../../lib/helpers/courses.helper';
import { ICourseOccupation } from '../../lib/interfaces/CourseOccupation';
import { getCourses } from '../../api/apis';
import { day } from '../../lib/helpers/time.helper';
import { RootState } from '../../redux/store';

// CSS
import './CoursesList.scss';

type CoursesListProps = {
	courseOccupations: ICourseOccupation[];
};

const CoursesList: FC<CoursesListProps> = ({ courseOccupations }) => {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const way: string = searchParams.get(COURSE_FILTERS.WAY) || '';
	const status: string | null = searchParams.get(COURSE_FILTERS.STATUS);
	const lastUrlParams = useSelector(
		(state: RootState) => state.ui.url.lastCourseParams,
	);

	useEffect(() => {
		if (way === '') {
			const wayParam = lastUrlParams?.way || courseOccupations[0].slug;
			let url = `/courses?way=${wayParam}`;
			if (status) {
				url = `${url}&status=${status}`;
			} else {
				url = lastUrlParams?.status
					? `${url}&status=${lastUrlParams?.status}`
					: url;
			}
			navigate(url);
		}
	}, [way]);

	const queryKey = `courses-${way}`;
	const { data, isError, error } = useQuery<Course[], Error>(
		[queryKey],
		() => getCourses(way),
		{
			enabled: !!courseOccupations && !!way,
			staleTime: day,
		},
	);

	if (isError) {
		return <span>Error: {error?.message}</span>;
	}

	if (!data) {
		return <Loader />;
	}

	// let courses: Course[] | undefined = way
	// 	? data
	// 			?.filter((course) => course.occupation?.includes(way))
	// 			.sort((a, b) => a.orderNumber - b.orderNumber)
	// 	: data;
	let courses = data;
	if (status) {
		if (status === COURSE_STATUSES.IN_PROGRESS) {
			courses = courses.filter(
				(course) => course.progress > 0 && course.progress < 100,
			);
		} else if (status === COURSE_STATUSES.NEW) {
			courses = courses.filter((course) => course.progress === 0);
		} else if (status === COURSE_STATUSES.COMPLETED) {
			courses = courses.filter((course) => course.progress === 100);
		}
	}

	return (
		<div className="CoursesList__wrapper">
			<div className="CoursesList__list">
				{courses?.map((course) => (
					<div key={course.slug} className="CoursesList__list__item">
						<CoursesListItem courseData={course} />
					</div>
				))}
				{courses.length === 0 && (
					<Typography variant="h6" component="h6">
						{t('noCoursesFound')}
					</Typography>
				)}
			</div>
		</div>
	);
};

export default CoursesList;
